import { SelectionModel } from '@angular/cdk/collections';
import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Subscription, firstValueFrom } from 'rxjs';
import { CaseAttachmentsComponent } from 'src/app/components/case-structure/case-attachments/case-attachments.component';
import { CaseEvidenceRecordsComponent } from 'src/app/components/case-structure/case-evidence-records/case-evidence-records.component';
import { CaseInformationComponent } from 'src/app/components/case-structure/case-information/case-information.component';
import { CaseNotesComponent } from 'src/app/components/case-structure/case-notes/case-notes.component';
import { CaseProofsComponent } from 'src/app/components/case-structure/case-proofs/case-proofs.component';
import { CaseUsersComponent } from 'src/app/components/case-structure/case-users/case-users.component';
import { DocumentsCaseComponent } from 'src/app/components/documents-display/documents-case/documents-case.component';
import { AddCaseDocumentsDialogComponent } from 'src/app/dialogs/add-case-documents-dialog/add-case-documents-dialog.component';
import { ConfirmationDialogComponent } from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';
import { Case, CaseDocument, CasePossiblePhase, CaseStatus, CaseType } from 'src/app/models/case';
import { Document } from 'src/app/models/document';
import { EvidenceRecord, getExtensionOfMIME } from 'src/app/models/evidenceRecord';
import { Paginator } from 'src/app/models/paginator';
import { PropertyOffice } from 'src/app/models/propertyOffice';
import { User } from 'src/app/models/user';
import { AclService, Scope } from 'src/app/services/acl.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { CaseCreationOrUpdate, CaseService } from 'src/app/services/case.service';
import { DocumentService } from 'src/app/services/document.service';
import { MessageService } from 'src/app/services/message.service';
import { PropertyOfficeService } from 'src/app/services/property-office.service';
import { UtilsService } from 'src/app/services/utils.service';
import {
  EvidenceRecordGenerationDialogComponent,
  REVIEW_PENDING_DOCUMENTS
} from '../../../dialogs/evidence-record-generation-dialog/evidence-record-generation-dialog.component';
import { ProductService } from 'src/app/models/productService';
import { TrademarkService } from 'src/app/services/trademark.service';
import { Note } from 'src/app/models/note';

@Component({
  selector: 'app-case',
  templateUrl: './case.component.html',
  styleUrls: ['./case.component.scss']
})
export class CaseComponent implements OnInit {
  public CaseType = CaseType;

  from: string = 'CASES';
  mode: 'CONSULTATION' | 'EDITION' = 'CONSULTATION';
  loading: boolean = false;

  case?: Case;

  productsServices?: ProductService[];
  trademarkProductsServices?: ProductService[];

  count: number = 0;

  possibleActions: CasePossiblePhase[] = [];

  propertyOffice?: PropertyOffice;

  comment?: string;

  selectedIndex: number = 0;

  numberOfAttachments: number = 0;

  documents: Paginator<CaseDocument> = {
    docs: [],
    page: 1,
    offset: 0,
    limit: 10,
    totalDocs: 0,
    sort: '-createdAt'
  };
  filteredDocuments: CaseDocument[] = [];

  selection = new SelectionModel<CaseDocument>(true, []);

  @ViewChild('caseTabs') caseTabs?: MatTabGroup;
  @ViewChild('caseInformation') caseInformation?: CaseInformationComponent;
  @ViewChild('caseProofs') caseProofs?: CaseProofsComponent;
  @ViewChild('caseEvidenceRecords') caseEvidenceRecords?: CaseEvidenceRecordsComponent;
  @ViewChild('caseAttachments') caseAttachments?: CaseAttachmentsComponent;
  @ViewChild('caseNotes') caseNotes?: CaseNotesComponent;
  @ViewChild('caseUsers') caseUsers?: CaseUsersComponent;

  @ViewChild('scrollable') scrollable?: ElementRef;

  noLinkedDocumentsButton: {
    label: string;
    icon: string;
    class: string;
    action: Function
  } = {
      label: 'CASES_LIST.NEW',
      icon: 'add_circle_outline',
      class: 'main-button',
      action: () => { this.router.navigate(['new'], { relativeTo: this.route }) }
    }

  private user?: User;

  focusedNote?: Note;

  private subscription: Subscription;

  isManagerOrAbove: boolean = false;
  relevantCountries: string[] = [];



  constructor(
    private _message: MessageService,
    private aclService: AclService,
    private authService: AuthenticationService,
    private caseService: CaseService,
    private dialog: MatDialog,
    private location: Location,
    private propertyOfficeService: PropertyOfficeService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private trademarkService: TrademarkService,
    private translate: TranslateService,
    private utils: UtilsService
  ) {
    this.authService.user.subscribe((user: User | undefined) => {
      this.user = user;
    });
    this.from = window.history.state.from || "CASES";
    this.mode = window.history.state.mode || "CONSULTATION";
    this.subscription = this._message.changeEmitted$.subscribe(change => {
      if (change && change.key == "CASE.REFRESH" && change.data && change.data.caseId && this.case && this.case._id && change.data.caseId === this.case?._id) {
        this.retrieveCase(this.case._id)
      }
    });
  }

  async ngOnInit() {
    this.isManagerOrAbove = this.aclService.isManagerOrAbove();
    const params = await firstValueFrom(this.route.params);
    const queryParams = await firstValueFrom(this.route.queryParams);
    await this.retrieveCase(params['id']);
    if (queryParams["download"]) {
      await this.downloadEvidenceRecord(queryParams["download"]);
      this.router.navigate([], {
        queryParams: {
          'download': null
        },
        queryParamsHandling: 'merge'
      })
    }
  }

  onTabChange(event: MatTabChangeEvent) {
    if (this.case?.type === CaseType.RENEWAL_MAINTENANCE) {
      switch (event.index) {
        case 1:
          this.caseEvidenceRecords?.init();
          break;
        case 2:
          this.caseUsers?.init();
          break;
        default:
          break;
      }
      return;
    }
    if (event.index === 1) {
      if (this.caseProofs) {
        this.caseProofs?.init();
      }
    } else if (event.index === 2) {
      if (this.caseEvidenceRecords) {
        this.caseEvidenceRecords?.init();
      }
    } else if (event.index === 3) {
      if (this.caseAttachments) {
        this.caseAttachments?.retrieveAttachments();
      }
    } else if (event.index === 4) {
      if (this.caseNotes) {
        this.caseNotes?.scroll();
      }
    } else if (event.index === 5) {
      if (this.caseUsers) {
        this.caseUsers?.init();
      }
    }
  }

  canUpdate() {
    return this.case?.state !== CaseStatus.CLOSED;
  }

  isUpdateDisabled() {
    return this.loading || (!this.case?.trademark && this.case?.trademark.ref === '')
      || (this.case?.niceClassification.length === 0)
      || (!this.case?.timePeriod || !this.case.timePeriod.start || !this.case.timePeriod.end)
  }

  async retrieveCase(id: string) {
    try {
      this._message.emitChange("LOADING", "START");
      this.loading = true;
      this.case = await this.caseService.retrieve(id);
      this.retrieveProductsServices();
      if (this.case && this.case.documents) {
        this.count = this.case.documents?.length;
      }
      if (this.caseProofs) {
        this.caseProofs?.init();
      }
      if (this.case?._id) {
        this.numberOfAttachments = await this.caseService.countAttachments(this.case._id);
        this.relevantCountries = await this.caseService.getRelevantTerritories(this.case._id);
      }
      if (this.case.propertyOffice) {
        this.propertyOffice = await this.propertyOfficeService.retrieveOne(this.case?.propertyOffice);
      }
      const tab = window.history.state.tab;
      if (tab && tab == 'PROOFS' && this.case.type !== CaseType.RENEWAL_MAINTENANCE) {
        this.selectedIndex = 1;
      }
      this.loading = false;
      this._message.emitChange("LOADING", "END");
    } catch (err) {
      if (err instanceof HttpErrorResponse && err.status === 404) {
        this.router.navigate(['errors', '404'])
      }
      this.loading = false;
      this._message.emitChange("LOADING", "END");
      this.toastr.error('ERRORS.GENERIC');
    }
  }


  async retrieveProductsServices() {
    if (this.case && this.case.trademark && this.case.trademark.ref) {
      this.trademarkProductsServices = await this.trademarkService.retrieveProductsAndServices(this.case.trademark.ref);
      this.productsServices = this.trademarkProductsServices.filter((ps) => ps._id && this.case?.productsServices?.map((cps) => cps.ref).includes(ps._id))
    }
  }

  /**
   * Toggle state of a case (OPENED/CLOSED)
   */
  async updateCaseState() {
    if (!this.case?._id) {
      this.toastr.error('ERRORS.GENERIC');
      return;
    };
    const target = this.case?.state === CaseStatus.OPENED ? CaseStatus.CLOSED : CaseStatus.OPENED;
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: target === CaseStatus.OPENED ? 'CASE.OPEN_CASE_TITLE' : 'CASE.CLOSE_CASE_TITLE',
        text: this.translate.instant(target === CaseStatus.OPENED ? 'CASE.OPEN_CASE_TEXT' : 'CASE.CLOSE_CASE_TEXT', { reference: this.case?.identifier || this.case?.reference }),
        button: {
          text: 'ACTIONS.CONFIRM',
          class: 'main-button'
        }
      }
    }
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
    if (result && result.confirmed && this.case) {
      try {
        this.loading = true;
        this._message.emitChange("LOADING", "START");
        const newCase = await this.caseService.updateCaseState(this.case._id, target);
        this.case = newCase;
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        this.toastr.success(this.translate.instant(target === CaseStatus.OPENED ? 'CASE.OPEN_CASE_SUCCESS' : 'CASE.CLOSE_CASE_SUCCESS', { reference: this.case?.identifier || this.case?.reference }));
      } catch (err) {
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        this.toastr.error('ERRORS.GENERIC');
      }
    }

  }



  async onRefresh() {
    if (this.case?._id) {
      this.retrieveCase(this.case._id);
      if (this.caseInformation) {
        this.caseInformation.refreshCoverage();
      }
      if (this.caseAttachments) {
        this.numberOfAttachments = await this.caseService.countAttachments(this.case._id);
      }
    }
  }


  openEditMode() {
    if (this.caseTabs) {
      this.caseTabs.selectedIndex = 0;
      this.mode = 'EDITION';
    }

  }


  async update() {
    try {
      if (this.case && this.case._id) {
        const caseId = this.case._id;
        this._message.emitChange("LOADING", "START");
        this.loading = true;
        const caseToUpdate: CaseCreationOrUpdate = {
          trademark: this.case.trademark.ref,
          propertyOffice: this.case.propertyOffice,
          niceClassification: this.case.niceClassification,
          productsServices: this.case.productsServices,
          identifier: this.case.identifier,
          timePeriod: {
            start: moment(this.case.timePeriod.start).format("YYYY-MM-DD"),
            end: moment(this.case.timePeriod.end).format("YYYY-MM-DD")
          },
          lang: this.case.lang,
          type: this.case.type,
          state: this.case.state
        }
        this.case = await this.caseService.update(caseId, caseToUpdate);
        this.relevantCountries = await this.caseService.getRelevantTerritories(caseId);
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        this.toastr.success(this.translate.instant('CASE.UPDATE_SUCCESS', { reference: this.case.identifier || this.case.reference }));
        this.mode = 'CONSULTATION';
      }
    } catch (err) {
      this.loading = false;
      this._message.emitChange("LOADING", "END");
      this.toastr.error('ERRORS.GENERIC');
    }
  }

  async delete() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: 'CASE.DELETE_CASE_TITLE',
        text: this.translate.instant('CASE.DELETE_CASE_TEXT', { reference: this.case?.identifier || this.case?.reference }),
        button: {
          text: 'ACTIONS.DELETE',
          class: 'danger-button'
        },
        confirmation: {
          text: 'CASE.DELETE_CASE_CONFIRMATION',
          value: this.case?.identifier || this.case?.reference
        }
      }
    }
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
    if (result && result.confirmed && this.case) {
      try {
        this.loading = true;
        this._message.emitChange("LOADING", "START");
        await this.caseService.delete(this.case._id!);
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        this.router.navigateByUrl('/cases');
        this.toastr.success(this.translate.instant('CASE.DELETE_CASE_SUCCESS', { reference: this.case?.identifier || this.case?.reference }));
      } catch (err) {
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        this.toastr.error('ERRORS.GENERIC');
      }
    }
  }

  async openEvidenceRecordGenerationDialog() {
    this.dialog.open(EvidenceRecordGenerationDialogComponent, {
      width: '640px',
      data: {
        case: this.case,
        allowGroupByCountry: this.relevantCountries.length > 1,
      }
    }).afterClosed()
      .subscribe({
        next: (validated) => {
          if (validated === REVIEW_PENDING_DOCUMENTS) {
            this.reviewPendingDocuments();
          } else if (validated && this.caseEvidenceRecords) {
            this.caseEvidenceRecords.init();
          }
        }
      });
  }

  /**
   * Back to the case's documents, after the user stopped an evidence record generation to approve or reject
   * the documents pending approval first. A renewal case lists its documents in the information tab.
   */
  reviewPendingDocuments() {
    this.selectedIndex = this.case?.type === CaseType.RENEWAL_MAINTENANCE ? 0 : 1;
    this.toastr.info('EVIDENCE_RECORDS.PENDING_DOCUMENTS_REVIEW_HINT', undefined, { timeOut: 10000 });
  }

  async generateEvidenceRecord() {
    try {
      if (this.case?._id) {
        this._message.emitChange("LOADING", "START");
        await this.caseService.generateEvidenceRecord(this.case?._id);
        if (this.caseEvidenceRecords) {
          this.caseEvidenceRecords.init();
        }
        this.toastr.success(this.translate.instant('EVIDENCE_RECORDS.GENERATION_SUCCESS_MESSAGE'), this.translate.instant('EVIDENCE_RECORDS.GENERATION_SUCCESS_TITLE'));
      }
      this._message.emitChange("LOADING", "END");
    }
    catch (err: any) {
      this._message.emitChange("LOADING", "END");
      if (err?.error?.message && this.utils.hasTranslation(`ERRORS.${err.error.message}`)) {
        this.toastr.error(`ERRORS.${err.error.message}`)
      } else {
        this.toastr.error(`ERRORS.GENERIC`)
      }
    }
  }

  async downloadEvidenceRecord(evidenceRecordId: string) {

    try {

      if (this.case && this.case._id && evidenceRecordId) {
        const evidenceRecord = await this.caseService.retrieveEvidenceRecord(this.case._id, evidenceRecordId);
        const base_name = `${this.case.identifier || this.case.reference}_evidence_record_${evidenceRecord.name}`;
        const toast = this.toastr.info(this.translate.instant('EVIDENCE_RECORDS.DOWNLOAD_MESSAGE', evidenceRecord), "EVIDENCE_RECORDS.DOWNLOAD_TITLE", {
          progressBar: true,
          disableTimeOut: true
        })
        const blob = await this.caseService.downloadEvidenceRecord(this.case._id, evidenceRecordId);

        const a = document.createElement('a')
        const objectUrl = URL.createObjectURL(blob)
        a.href = objectUrl
        a.download = `${base_name}${evidenceRecord.mimeType ? getExtensionOfMIME(evidenceRecord.mimeType) : '.zip'}`;
        a.click();
        this.toastr.clear(toast.toastId);
        URL.revokeObjectURL(objectUrl);
      }
    } catch (err) {
      this._message.emitChange("LOADING", "END");
      this.toastr.error(`ERRORS.GENERIC`)
    }
  }

}
